/**
 * 关键词库 API 服务
 */
import axios from 'axios';
import {
    TargetingKeyword,
    NegativeKeyword,
    KeywordPerformanceHistory,
    KeywordLibraryStats,
    BulkImportResult,
    Marketplace,
} from './types';

const API_BASE = '/api';

// ==================== 投放词库 API ====================

export const targetingKeywordApi = {
    // 获取列表
    list: async (params?: {
        sku_id?: number;
        marketplace?: Marketplace;
        keyword_type?: string;
        match_type?: string;
        is_active?: boolean;
        search?: string;
    }): Promise<TargetingKeyword[]> => {
        const response = await axios.get(`${API_BASE}/targeting-keywords/`, { params });
        return Array.isArray(response.data) ? response.data : response.data.results || [];
    },

    // 获取单个
    get: async (id: number): Promise<TargetingKeyword> => {
        const response = await axios.get(`${API_BASE}/targeting-keywords/${id}/`);
        return response.data;
    },

    // 创建
    create: async (data: Partial<TargetingKeyword>): Promise<TargetingKeyword> => {
        const response = await axios.post(`${API_BASE}/targeting-keywords/`, data);
        return response.data;
    },

    // 更新
    update: async (id: number, data: Partial<TargetingKeyword>): Promise<TargetingKeyword> => {
        const response = await axios.patch(`${API_BASE}/targeting-keywords/${id}/`, data);
        return response.data;
    },

    // 删除
    delete: async (id: number): Promise<void> => {
        await axios.delete(`${API_BASE}/targeting-keywords/${id}/`);
    },

    // 批量导入
    bulkImport: async (
        sku_id: number,
        marketplace: Marketplace,
        keywords: Array<{
            keyword: string;
            keyword_type?: string;
            match_type?: string;
            conversion_rate?: number;
            search_volume?: number;
            priority?: number;
            notes?: string;
        }>
    ): Promise<BulkImportResult> => {
        const response = await axios.post(`${API_BASE}/targeting-keywords/bulk_import/`, {
            sku_id,
            marketplace,
            keywords,
        });
        return response.data;
    },

    // 获取统计
    stats: async (params?: {
        sku_id?: number;
        marketplace?: Marketplace;
    }): Promise<KeywordLibraryStats> => {
        const response = await axios.get(`${API_BASE}/targeting-keywords/stats/`, { params });
        return response.data;
    },

    // 批量更新状态
    batchUpdateStatus: async (ids: number[], is_active: boolean): Promise<{ updated: number }> => {
        const response = await axios.post(`${API_BASE}/targeting-keywords/batch_update_status/`, {
            ids,
            is_active,
        });
        return response.data;
    },

    // 批量删除
    batchDelete: async (ids: number[]): Promise<{ deleted: number }> => {
        const response = await axios.delete(`${API_BASE}/targeting-keywords/batch_delete/`, {
            data: { ids },
        });
        return response.data;
    },
};

// ==================== 否定词库 API ====================

export const negativeKeywordApi = {
    // 获取列表
    list: async (params?: {
        sku_id?: number;
        marketplace?: Marketplace;
        negative_type?: string;
        reason?: string;
        is_active?: boolean;
        search?: string;
    }): Promise<NegativeKeyword[]> => {
        const response = await axios.get(`${API_BASE}/negative-keywords/`, { params });
        return Array.isArray(response.data) ? response.data : response.data.results || [];
    },

    // 获取单个
    get: async (id: number): Promise<NegativeKeyword> => {
        const response = await axios.get(`${API_BASE}/negative-keywords/${id}/`);
        return response.data;
    },

    // 创建
    create: async (data: Partial<NegativeKeyword>): Promise<NegativeKeyword> => {
        const response = await axios.post(`${API_BASE}/negative-keywords/`, data);
        return response.data;
    },

    // 更新
    update: async (id: number, data: Partial<NegativeKeyword>): Promise<NegativeKeyword> => {
        const response = await axios.patch(`${API_BASE}/negative-keywords/${id}/`, data);
        return response.data;
    },

    // 删除
    delete: async (id: number): Promise<void> => {
        await axios.delete(`${API_BASE}/negative-keywords/${id}/`);
    },

    // 批量导入
    bulkImport: async (
        sku_id: number,
        marketplace: Marketplace,
        keywords: Array<{
            content: string;
            negative_type?: string;
            reason?: string;
            level?: string;
            notes?: string;
        }>
    ): Promise<BulkImportResult> => {
        const response = await axios.post(`${API_BASE}/negative-keywords/bulk_import/`, {
            sku_id,
            marketplace,
            keywords,
        });
        return response.data;
    },

    // 获取统计
    stats: async (params?: {
        sku_id?: number;
        marketplace?: Marketplace;
    }): Promise<KeywordLibraryStats> => {
        const response = await axios.get(`${API_BASE}/negative-keywords/stats/`, { params });
        return response.data;
    },

    // 批量更新状态
    batchUpdateStatus: async (ids: number[], is_active: boolean): Promise<{ updated: number }> => {
        const response = await axios.post(`${API_BASE}/negative-keywords/batch_update_status/`, {
            ids,
            is_active,
        });
        return response.data;
    },

    // 批量删除
    batchDelete: async (ids: number[]): Promise<{ deleted: number }> => {
        const response = await axios.delete(`${API_BASE}/negative-keywords/batch_delete/`, {
            data: { ids },
        });
        return response.data;
    },
};

// ==================== 关键词表现历史 API ====================

export const keywordHistoryApi = {
    // 获取列表
    list: async (params?: {
        targeting_keyword_id?: number;
        start_date?: string;
        end_date?: string;
    }): Promise<KeywordPerformanceHistory[]> => {
        const response = await axios.get(`${API_BASE}/keyword-performance-history/`, { params });
        return Array.isArray(response.data) ? response.data : response.data.results || [];
    },

    // 批量导入
    bulkImport: async (records: Array<{
        targeting_keyword_id: number;
        record_date: string;
        impressions?: number;
        clicks?: number;
        spend?: number;
        orders?: number;
        sales?: number;
    }>): Promise<{ created: number; errors: string[]; total: number }> => {
        const response = await axios.post(`${API_BASE}/keyword-performance-history/bulk_import/`, {
            records,
        });
        return response.data;
    },
};
